import { Capacitor } from '@capacitor/core'
import { SplashScreen } from '@capacitor/splash-screen'

export const UPDATE_RELOAD_PENDING_KEY = 'gmt_update_reload_pending'
export const UPDATE_RELOAD_PENDING_CLASS = 'gmt-update-reload-pending'
export const UPDATE_RELOAD_SPINNER_VISIBLE_CLASS = 'gmt-spinner-visible'
const SPINNER_REVEAL_DELAY_MS = 320

function isUpdateReloadPending(): boolean {
  try {
    return sessionStorage.getItem(UPDATE_RELOAD_PENDING_KEY) === '1'
  } catch {
    return false
  }
}

function getUpdateSplashElement(): HTMLElement | null {
  return document.getElementById('gmt-update-splash')
}

export function markUpdateReloadPending(): void {
  try {
    sessionStorage.setItem(UPDATE_RELOAD_PENDING_KEY, '1')
  } catch {
    /* ignore */
  }
}

export function clearUpdateReloadPending(): void {
  try {
    sessionStorage.removeItem(UPDATE_RELOAD_PENDING_KEY)
    document.documentElement.classList.remove(UPDATE_RELOAD_PENDING_CLASS)
    getUpdateSplashElement()?.classList.remove(UPDATE_RELOAD_SPINNER_VISIBLE_CLASS)
    document.getElementById('gmt-update-pending-boot')?.remove()
  } catch {
    /* ignore */
  }
}

/** Show logo-only overlay (spinner reveals separately so the logo never jumps). */
export function showUpdateReloadSplash(): void {
  try {
    document.documentElement.classList.add('dark', UPDATE_RELOAD_PENDING_CLASS)
    document.documentElement.style.backgroundColor = '#262626'
    if (document.body) {
      document.body.style.backgroundColor = '#262626'
    }
    const el = getUpdateSplashElement()
    if (!el) return
    el.style.display = 'block'
    el.classList.remove(UPDATE_RELOAD_SPINNER_VISIBLE_CLASS)
  } catch {
    /* ignore */
  }
}

export function revealUpdateReloadSpinner(): void {
  getUpdateSplashElement()?.classList.add(UPDATE_RELOAD_SPINNER_VISIBLE_CLASS)
}

export function scheduleUpdateReloadSpinnerReveal(delayMs = SPINNER_REVEAL_DELAY_MS): void {
  window.setTimeout(revealUpdateReloadSpinner, delayMs)
}

function waitForPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve())
    })
  })
}

/** Drop native splash once the HTML logo has painted; then fade the spinner in underneath. */
export async function dismissNativeSplashForUpdateReload(): Promise<void> {
  if (!isUpdateReloadPending()) return

  showUpdateReloadSplash()
  await waitForPaint()

  if (Capacitor.isNativePlatform()) {
    for (let attempt = 0; attempt < 8; attempt += 1) {
      try {
        await SplashScreen.hide({ fadeOutDuration: 0 })
        break
      } catch {
        await new Promise((resolve) => setTimeout(resolve, 16))
      }
    }
  }

  scheduleUpdateReloadSpinnerReveal()
}

/**
 * Bust WebView HTTP cache (Android especially). Plain reload() often reuses stale JS.
 */
function performHardReload(): void {
  const url = new URL(window.location.href)
  url.searchParams.set('gmt_reload', String(Date.now()))
  window.location.replace(url.toString())
}

async function reloadWithUpdateSplashAsync(): Promise<void> {
  markUpdateReloadPending()
  showUpdateReloadSplash()

  // SplashScreen.show() before navigation can block Android WebView reloads.
  const platform = Capacitor.getPlatform()
  if (Capacitor.isNativePlatform() && platform === 'ios') {
    try {
      await SplashScreen.show({ autoHide: false, fadeInDuration: 0 })
    } catch {
      /* ignore */
    }
  }

  await waitForPaint()
  performHardReload()
}

/** Full reload after marking pending so index.html shows the launch-style overlay until the app is ready. */
export function reloadWithUpdateSplash(): void {
  void reloadWithUpdateSplashAsync()
}

export function removeUpdateReloadSplashElement(): void {
  getUpdateSplashElement()?.remove()
}
